import { useState, useEffect } from "react";
import { Save01, RefreshCw01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Select } from "@/components/base/select/select";
import { Toggle } from "@/components/base/toggle/toggle";
import { toast } from "@/lib/toast-service";
import { apiClient } from "@/lib/api-client";

interface MenuItemConfig {
  key: string;
  enabled: boolean;
  label: string;
}

interface CompanyTypeInfo {
  id: string;
  label: string;
}

interface CompanyTypesResponse {
  company_types: CompanyTypeInfo[];
}

const NON_MODIFIABLE_MENU_ITEMS = ["controlHub", "settings", "activity"];

function formatMenuItemLabel(key: string, companyType?: string): string {
  const labelMap: Record<string, string> = {
    "ask-eve": "Ask Eve",
    controlHub: "Control Hub",
    addressBook: "Address Book",
  };

  if (
    key === "dashboard" &&
    companyType &&
    ["sender", "shipper", "receiver", "freight_forwarder"].includes(companyType)
  ) {
    return "Control Hub";
  }

  if (labelMap[key]) {
    return labelMap[key];
  }

  const formatted = key.replace(/([A-Z])/g, " $1").trim();
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

export function MenuConfigTab() {
  const [companyTypes, setCompanyTypes] = useState<CompanyTypeInfo[]>([]);
  const [selectedCompanyType, setSelectedCompanyType] = useState<string>("");
  const [menuItems, setMenuItems] = useState<MenuItemConfig[]>([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isLoadingCompanyTypes, setIsLoadingCompanyTypes] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  useEffect(() => {
    loadCompanyTypes();
  }, []);

  useEffect(() => {
    if (selectedCompanyType) {
      loadMenuConfiguration(selectedCompanyType);
    }
  }, [selectedCompanyType]);

  const loadCompanyTypes = async () => {
    setIsLoadingCompanyTypes(true);
    try {
      const data = await apiClient<CompanyTypesResponse>(
        "/menu-configurations/company-types",
        { requireAuth: true }
      );
      setCompanyTypes(data.company_types);
      setSelectedCompanyType((prev) =>
        data.company_types.length > 0 && !prev
          ? data.company_types[0].id
          : prev
      );
    } catch (error) {
      console.error("Error loading company types:", error);
      toast.error("Failed to load company types");
    } finally {
      setIsLoadingCompanyTypes(false);
    }
  };

  const loadMenuConfiguration = async (companyType: string) => {
    setIsLoadingConfig(true);
    try {
      const data = await apiClient<{ items: Record<string, boolean> }>(
        `/menu-configurations/by-type/${companyType}`,
        { requireAuth: true }
      );

      const items: MenuItemConfig[] = Object.entries(data.items).map(
        ([key, enabled]) => ({
          key,
          enabled: NON_MODIFIABLE_MENU_ITEMS.includes(key)
            ? true
            : (enabled as boolean),
          label: formatMenuItemLabel(key, companyType),
        })
      );

      setMenuItems(items);
    } catch (error) {
      console.error("Error loading menu configuration:", error);
      toast.error("Failed to load menu configuration");
      setMenuItems([]);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const handleToggleMenuItem = (key: string) => {
    if (NON_MODIFIABLE_MENU_ITEMS.includes(key)) {
      return;
    }

    setMenuItems((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, enabled: !item.enabled } : item
      )
    );
  };

  const handleSaveConfiguration = async () => {
    setIsSavingConfig(true);
    try {
      const configObject = menuItems.reduce(
        (acc, item) => {
          acc[item.key] =
            NON_MODIFIABLE_MENU_ITEMS.includes(item.key) ? true : item.enabled;
          return acc;
        },
        {} as Record<string, boolean>
      );

      await apiClient<{ items: Record<string, boolean> }>(
        "/menu-configurations/global",
        {
          method: "POST",
          requireAuth: true,
          body: JSON.stringify({
            company_type: selectedCompanyType,
            items: configObject,
          }),
        }
      );

      toast.success("Menu configuration saved successfully");
    } catch (error) {
      console.error("Error saving menu configuration:", error);
      toast.error("Failed to save menu configuration");
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleResetConfiguration = () => {
    loadMenuConfiguration(selectedCompanyType);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-secondary bg-primary p-6">
        <h3 className="text-lg font-semibold text-primary mb-1">
          Global Menu Configuration
        </h3>
        <p className="text-tertiary text-sm mb-6">
          Configure which menu items are available for each company type. These
          settings apply globally to all companies of the selected type.
        </p>

        <div className="mb-6">
          {isLoadingCompanyTypes ? (
            <div className="text-tertiary">Loading company types...</div>
          ) : (
            <Select
              label="Company Type"
              items={companyTypes}
              selectedKey={selectedCompanyType}
              onSelectionChange={(key) =>
                setSelectedCompanyType(key as string)
              }
              size="md"
              className="max-w-xs"
              isDisabled={companyTypes.length === 0}
            >
              {(item) => (
                <Select.Item key={item.id} id={item.id}>
                  {item.label}
                </Select.Item>
              )}
            </Select>
          )}
        </div>

        {isLoadingConfig ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-tertiary">Loading configuration...</div>
          </div>
        ) : (
          <>
            <div className="space-y-3 mb-6">
              {menuItems.length === 0 ? (
                <div className="text-center py-8 text-tertiary">
                  No menu items available for this company type
                </div>
              ) : (
                <>
                  {menuItems
                    .filter((item) =>
                      NON_MODIFIABLE_MENU_ITEMS.includes(item.key)
                    )
                    .map((item) => (
                      <div
                        key={item.key}
                        className="flex items-center justify-between py-3 px-4 bg-secondary rounded-lg opacity-75"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-primary">
                              {item.label}
                            </p>
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                              Always Enabled
                            </span>
                          </div>
                          <p className="text-xs text-tertiary">Key: {item.key}</p>
                        </div>
                        <Toggle
                          isSelected={item.enabled}
                          onChange={() => handleToggleMenuItem(item.key)}
                          aria-label={`Toggle ${item.label}`}
                          isDisabled={true}
                        />
                      </div>
                    ))}

                  {menuItems.some(
                    (item) => !NON_MODIFIABLE_MENU_ITEMS.includes(item.key)
                  ) && (
                    <div className="border-t border-secondary my-4"></div>
                  )}

                  {menuItems
                    .filter(
                      (item) => !NON_MODIFIABLE_MENU_ITEMS.includes(item.key)
                    )
                    .map((item) => (
                      <div
                        key={item.key}
                        className="flex items-center justify-between py-3 px-4 bg-secondary rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-primary">
                            {item.label}
                          </p>
                          <p className="text-xs text-tertiary">
                            Key: {item.key}
                          </p>
                        </div>
                        <Toggle
                          isSelected={item.enabled}
                          onChange={() => handleToggleMenuItem(item.key)}
                          aria-label={`Toggle ${item.label}`}
                        />
                      </div>
                    ))}
                </>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Button
                color="primary"
                onClick={handleSaveConfiguration}
                disabled={isSavingConfig || menuItems.length === 0}
                iconLeading={Save01}
                size="md"
              >
                {isSavingConfig ? "Saving..." : "Save Configuration"}
              </Button>
              <Button
                color="secondary"
                onClick={handleResetConfiguration}
                disabled={isLoadingConfig}
                iconLeading={RefreshCw01}
                size="md"
              >
                Reset
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="rounded-lg border border-secondary bg-primary p-6">
        <h3 className="text-lg font-semibold text-primary mb-1">
          Company-Specific Overrides
        </h3>
        <p className="text-tertiary text-sm mb-4">
          To configure menu overrides for specific companies (e.g., demo
          accounts, beta testers), edit the company directly from the Companies
          page.
        </p>
        <Button
          color="secondary"
          onClick={() => (window.location.href = "/admin/companies")}
          size="md"
        >
          Go to Companies
        </Button>
      </div>
    </div>
  );
}
